/**
 * Viewport localStorage cache for instant restore on canvas load.
 * Eliminates 200-500ms visual jump from default viewport to API viewport.
 */

const VIEWPORT_STORAGE_PREFIX = 'genie:viewport:'
const VIEWPORT_MAX_AGE = 30 * 24 * 60 * 60 * 1000 // 30 days

interface CachedViewport {
  x: number
  y: number
  zoom: number
  timestamp: number
}

type Viewport = { x: number; y: number; zoom: number }

export function saveViewportToLocalStorage(canvasId: string, viewport: Viewport): void {
  try {
    const key = `${VIEWPORT_STORAGE_PREFIX}${canvasId}`
    const value: CachedViewport = { ...viewport, timestamp: Date.now() }
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

export function getViewportFromLocalStorage(canvasId: string): Viewport | null {
  try {
    const key = `${VIEWPORT_STORAGE_PREFIX}${canvasId}`
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const cached: CachedViewport = JSON.parse(raw)

    // Reject stale entries (> 30 days)
    if (Date.now() - cached.timestamp > VIEWPORT_MAX_AGE) {
      localStorage.removeItem(key)
      return null
    }

    return { x: cached.x, y: cached.y, zoom: cached.zoom }
  } catch {
    return null
  }
}

export function cleanStaleViewports(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(VIEWPORT_STORAGE_PREFIX))
    for (const key of keys) {
      try {
        const cached = JSON.parse(localStorage.getItem(key) || '')
        if (Date.now() - cached.timestamp > VIEWPORT_MAX_AGE) {
          localStorage.removeItem(key)
        }
      } catch {
        localStorage.removeItem(key) // Corrupted entry
      }
    }
  } catch {
    // localStorage unavailable — silent fail
  }
}
